import {BeraterUebersicht} from '../../shared/model/beraterUebersicht.model';
import {IFaehigkeit} from '../../shared/model/faehigkeit.model';
import {IProjekt} from '../../shared/model/projekt.model';
import {VocabularyService} from '../../services/vocabulary.service';

/** Die angezeigten Namen: gesucht wird, was auf dem Bildschirm steht (NP20-1170). */
export type VocabularyNames = Pick<VocabularyService, 'industryName' | 'mainFocusName' | 'methodName' | 'skillName' | 'skillCategory'>;

/**
 * Die Bereichsauswahl der Suche braucht dieselbe Feldliste in drei Gruppen: Namensfelder,
 * Positionsfeld und alle übrigen Angaben. Die Gruppe steht deshalb an jedem Eintrag, statt
 * die Liste ein zweites Mal aufzuschreiben.
 */
type SearchGroup = 'name' | 'position' | 'other';

interface SearchEntry {
  group: SearchGroup;
  text: string | undefined;
}

const named = (text: string | undefined): SearchEntry => ({group: 'name', text});
const positioned = (text: string | undefined): SearchEntry => ({group: 'position', text});
const other = (text: string | undefined): SearchEntry => ({group: 'other', text});

/**
 * Worin die Beraterübersicht sucht — die einzige Stelle, die das festlegt. Ein neues Feld am
 * Modell erweitert die Suche nicht von selbst, und was hier fehlt, liefert keine Treffer:
 * interne Kennungen, Betreuer, Notizen, Datumsangaben, Kennzeichen und Zahlen.
 *
 * Die gesprochenen Sprachen stehen hier mit ihrem gespeicherten Wert; der ausgeschriebene
 * Sprachname kommt übersetzt aus der Übersicht dazu.
 */
export function consultantSearchTexts(user: BeraterUebersicht, vocabulary: VocabularyNames): string[] {
  return textsOf(searchEntries(user, vocabulary));
}

/** Vor- und Nachname — für den Bereich „Name" und für die Namensprüfung in „Alle Felder". */
export function consultantNameTexts(user: BeraterUebersicht): string[] {
  return textsOf(nameEntries(user));
}

/** Das Positionsfeld — für den Bereich „Position". */
export function consultantPositionTexts(user: BeraterUebersicht): string[] {
  return textsOf(positionEntries(user));
}

/** Alles aus der Feldliste außer Name und Position. */
export function consultantOtherTexts(user: BeraterUebersicht, vocabulary: VocabularyNames): string[] {
  return textsOf(searchEntries(user, vocabulary).filter(entry => entry.group === 'other'));
}

function searchEntries(user: BeraterUebersicht, vocabulary: VocabularyNames): SearchEntry[] {
  return [
    ...nameEntries(user),
    other(user?.userAbbreviation),
    ...positionEntries(user),
    other(user?.wohnort),
    other(user?.firma),
    other(user?.nationalitaet),
    ...(user?.sprachen ?? []).map((e) => other(typeof e === 'string' ? e : e.language)),
    ...(user?.branchen ?? []).map(industry => other(vocabulary.industryName(industry))),
    ...(user?.schwerpunkte ?? []).map(focus => other(vocabulary.mainFocusName(focus))),
    ...skillTexts(user?.faehigkeiten, vocabulary),
    ...(user?.projekte ?? []).flatMap(projekt => projectTexts(projekt, vocabulary)),
    ...(user?.zertifikate ?? []).flatMap(certificate => [
      other(certificate?.lehrgang),
      other(certificate?.schulungsanbieter),
      other(certificate?.trainingsanbieter),
      other(certificate?.zertifikatnummer),
    ]),
    ...(user?.schulungen ?? []).flatMap(training => [
      other(training?.lehrgang),
      other(training?.schulungsanbieter),
    ]),
    ...(user?.werdegang ?? []).flatMap(station => [
      other(station?.unternehmen),
      other(station?.stellenbezeichnung),
      other(station?.ort),
      other(station?.taetigkeit),
    ]),
    ...(user?.ausbildung ?? []).flatMap(education => [
      other(education?.ausbildungseinrichtung),
      other(education?.ausbildungsgang),
      other(education?.ort),
      other(education?.abschluss),
    ]),
  ];
}

function nameEntries(user: BeraterUebersicht): SearchEntry[] {
  return [named(user?.name), named(user?.vorname), named(user?.nachname)];
}

function positionEntries(user: BeraterUebersicht): SearchEntry[] {
  return [positioned(user?.position)];
}

function textsOf(entries: SearchEntry[]): string[] {
  return entries.map(entry => entry.text).filter((value): value is string => !!value?.trim());
}

function skillTexts(skills: IFaehigkeit[] | undefined, vocabulary: VocabularyNames): SearchEntry[] {
  return (skills ?? []).flatMap(skill => [
    other(vocabulary.skillCategory(skill)),
    other(vocabulary.skillName(skill)),
  ]);
}

function projectTexts(projekt: IProjekt, vocabulary: VocabularyNames): SearchEntry[] {
  return [
    other(projekt?.projektname),
    other(projekt?.kunde),
    other(projekt?.stelle),
    other(projekt?.beschreibung),
    other(projekt?.workLocation),
    ...(projekt?.methodik ?? []).map(method => other(vocabulary.methodName(method))),
    ...(projekt?.tasks ?? []).flatMap(task => [other(task?.caption), other(task?.details)]),
    ...(projekt?.branchNames ?? []).map(industry => other(vocabulary.industryName(industry))),
    ...skillTexts(projekt?.faehigkeiten, vocabulary),
  ];
}
